import { useEffect, useState } from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import { Loader } from "../Loader";
import { useQuery } from "@tanstack/react-query";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector } from "src/react-query/select";
import { paymentGatewayDetailsUrl, paymentGatewayDetailByIdUrl } from "src/react-query/endPoints";
import { useMutate } from "src/hooks/useFetch.hook";
import { alertSlice } from "src/store/alert/alert.slice";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { getPaymentGatewayDefaultValues } from "./PaymentGateway.constants";
import { is } from "src/util/is";
import Countries from "src/data/countries.json";

const validationSchema = Yup.object({
  payment_gateway_id: Yup.number()
    .required("Payment Gateway ID is required")
    .typeError("Payment Gateway ID must be a number"),
  key_id: Yup.string().required("Key ID is required"),
  key_secret: Yup.string().required("Key Secret is required"),
  webhook_secret: Yup.string(),
  currency: Yup.string(),
  country: Yup.string(),
  gateway_type: Yup.string(),
  name: Yup.string(),
  domain: Yup.string(),
});

export const PaymentGatewayForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isCreate = useMatch("/payment-gateways/new");
  const { mutate, isLoading } = useMutate("paymentGatewayDetails");
  const [showKeySecret, setShowKeySecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  const { data: paymentGateway, isFetching } = useQuery({
    queryKey: queryKeys.paymentGatewayDetail(id),
    queryFn: fetchWithError,
    select: selector,
    enabled: !isCreate && !is.empty(id),
  });

  const formik = useFormik({
    initialValues: getPaymentGatewayDefaultValues(),
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const url = isCreate
        ? paymentGatewayDetailsUrl
        : paymentGatewayDetailByIdUrl(id);
      const method = isCreate ? "POST" : "PUT";

      mutate(
        {
          url,
          method,
          body: values,
        },
        {
          onSuccess: (response) => {
            dispatch(
              alertSlice.actions.setAlert({
                open: true,
                message: isCreate
                  ? "Payment gateway created successfully"
                  : "Payment gateway updated successfully",
                severity: "success",
              })
            );
            queryClient.invalidateQueries(queryKeys.paymentGatewayDetails());
            navigate("/payment-gateways");
            setSubmitting(false);
          },
          onError: (error) => {
            dispatch(
              alertSlice.actions.setAlert({
                open: true,
                message: error?.message || "Failed to save payment gateway",
                severity: "error",
              })
            );
            setSubmitting(false);
          },
        }
      );
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  useEffect(() => {
    if (paymentGateway && !isCreate && !isFetching) {
      formik.setValues({
        payment_gateway_id: paymentGateway.payment_gateway_id || "",
        key_id: paymentGateway.key_id || "",
        key_secret: paymentGateway.key_secret || "",
        webhook_secret: paymentGateway.webhook_secret || "",
        currency: paymentGateway.currency || "INR",
        country: paymentGateway.country || "IN",
        gateway_type: paymentGateway.gateway_type || "",
        name: paymentGateway.name || "",
        domain: paymentGateway.domain || "",
      });
    }
  }, [paymentGateway, isCreate, isFetching]);

  if (isFetching && !isCreate) return <Loader />;

  return (
    <Card sx={{ p: 3 }}>
      <FormikProvider value={formik}>
        <Form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6">Payment Gateway Details</Typography>
            </Grid>

            {/* Payment Gateway ID */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Payment Gateway ID"
                type="number"
                {...getFieldProps("payment_gateway_id")}
                error={Boolean(touched.payment_gateway_id && errors.payment_gateway_id)}
                helperText={touched.payment_gateway_id && errors.payment_gateway_id}
              />
            </Grid>

            {/* Gateway Type */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Gateway Type"
                {...getFieldProps("gateway_type")}
                error={Boolean(touched.gateway_type && errors.gateway_type)}
                helperText={touched.gateway_type && errors.gateway_type}
                placeholder="e.g., EASEBUZZ, STRIPE"
              />
            </Grid>

            {/* Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                {...getFieldProps("name")}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>

            {/* Domain */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Domain"
                {...getFieldProps("domain")}
                error={Boolean(touched.domain && errors.domain)}
                helperText={touched.domain && errors.domain}
              />
            </Grid>

            {/* Key ID */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Key ID"
                {...getFieldProps("key_id")}
                error={Boolean(touched.key_id && errors.key_id)}
                helperText={touched.key_id && errors.key_id}
                required
              />
            </Grid>

            {/* Key Secret */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Key Secret"
                type={showKeySecret ? "text" : "password"}
                {...getFieldProps("key_secret")}
                error={Boolean(touched.key_secret && errors.key_secret)}
                helperText={touched.key_secret && errors.key_secret}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowKeySecret((prev) => !prev)}
                        edge="end"
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <Icon icon={showKeySecret ? eyeFill : eyeOffFill} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Webhook Secret */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Webhook Secret"
                type={showWebhookSecret ? "text" : "password"}
                {...getFieldProps("webhook_secret")}
                error={Boolean(touched.webhook_secret && errors.webhook_secret)}
                helperText={touched.webhook_secret && errors.webhook_secret}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowWebhookSecret((prev) => !prev)}
                        edge="end"
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <Icon icon={showWebhookSecret ? eyeFill : eyeOffFill} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Currency */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  {...getFieldProps("currency")}
                  value={values.currency || "INR"}
                  label="Currency"
                >
                  <MenuItem value="INR">INR - Indian Rupee</MenuItem>
                  <MenuItem value="USD">USD - US Dollar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                  <MenuItem value="GBP">GBP - British Pound</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  {...getFieldProps("country")}
                  value={values.country || "IN"}
                  label="Country"
                >
                  {Countries.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name} ({country.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting || isLoading}
                >
                  {isCreate ? "Create" : "Update"}
                </LoadingButton>
                <LoadingButton
                  size="large"
                  type="button"
                  variant="outlined"
                  onClick={() => navigate("/payment-gateways")}
                >
                  Cancel
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Form>
      </FormikProvider>
    </Card>
  );
};

